import React, { useState } from 'react';
import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    makeStyles,
    withStyles,
} from '@material-ui/core';
import { connect } from 'react-redux';
import HttpService from '../../services/httpService';
import { setLoading, setSnackInfo } from '../../redux/actions/appAction';

const NAVY = '#1C375D';
const ORANGE = '#F47E46';

const getItemName = (item = {}) =>
    String(item.description || item.material || item.orderName || item.pieceName || '').trim();

const StyledTableCell = withStyles(() => ({
    head: {
        backgroundColor: NAVY,
        color: '#FFF2EB',
        fontWeight: 600,
        textAlign: 'center',
        whiteSpace: 'nowrap',
        border: '1px solid #eee5e5',
        padding: '10px 12px',
        fontSize: 13,
    },
    body: {
        fontSize: 13,
        color: NAVY,
        textAlign: 'center',
        border: '1px solid #eee5e5',
        padding: '8px 12px',
        backgroundColor: '#fff',
    },
}))(TableCell);

const useStyles = makeStyles((theme) => ({
    root: { padding: theme.spacing(2) },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
        marginBottom: theme.spacing(2),
    },
    title: {
        color: NAVY,
        fontWeight: 700,
        margin: 0,
    },
    searchRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
    },
    search: { minWidth: 280 },
    actionBtn: {
        backgroundColor: ORANGE,
        color: '#fff',
        fontWeight: 600,
        textTransform: 'none',
        '&:hover': { backgroundColor: '#E0663D' },
    },
    summaryCards: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: 12,
        marginBottom: theme.spacing(2),
    },
    summaryCard: {
        border: '1px solid #e8edf3',
        borderRadius: 10,
        padding: '12px 14px',
        backgroundColor: '#fafbfd',
    },
    summaryLabel: {
        fontSize: 11,
        fontWeight: 600,
        color: '#7b8794',
        textTransform: 'uppercase',
        letterSpacing: '0.4px',
        marginBottom: 4,
    },
    summaryValue: {
        fontSize: 16,
        fontWeight: 700,
        color: NAVY,
    },
    tableContainer: { overflowX: 'auto' },
    remainingZero: { color: '#c0392b', fontWeight: 600 },
    remainingPositive: { color: '#2e6b2e', fontWeight: 600 },
    matchChip: {
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
    },
}));

const getMatchChipStyle = (status = '') => {
    const normalized = String(status).toLowerCase();
    if (normalized === 'matched') return { backgroundColor: '#edf7ed', color: '#2e6b2e' };
    if (normalized === 'ambiguous') return { backgroundColor: '#fff4eb', color: '#c45a1f' };
    return { backgroundColor: '#fdecea', color: '#c0392b' };
};

const HardwareBomViewer = (props) => {
    const classes = useStyles();
    const [projectNo, setProjectNo] = useState('');
    const [activeProjectNo, setActiveProjectNo] = useState('');
    const [filter, setFilter] = useState('');
    const [meta, setMeta] = useState(null);
    const [items, setItems] = useState([]);

    const loadHardwareBom = async (projectNumber = projectNo) => {
        const normalized = String(projectNumber || '').trim();
        if (!normalized) {
            props.setSnackInfo('Enter a project number', 'warning');
            return;
        }

        props.setLoading(true);
        try {
            const res = await HttpService.getHardwareBomByProjectNumber(normalized);
            const data = res.data || {};
            setMeta(data);
            setItems(data.items || []);
            setActiveProjectNo(data.projectNumber || normalized);
            setFilter('');
        } catch (error) {
            setMeta(null);
            setItems([]);
            setActiveProjectNo('');
            props.setSnackInfo(
                error?.response?.data?.message || 'Failed to load hardware BOM',
                'error'
            );
        }
        props.setLoading(false);
    };

    const filteredItems = items.filter((item) => {
        if (!filter.trim()) return true;
        const q = filter.trim().toLowerCase();
        const haystack = [
            getItemName(item),
            item.sheetName,
            item.subCategory,
            item.materialCode,
        ].join(' ').toLowerCase();
        return haystack.includes(q);
    });

    const matchedCount = items.filter((item) => item.matchStatus === 'matched').length;

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <h1 className={classes.title}>Hardware BOM (IMOS)</h1>
                <div className={classes.searchRow}>
                    <TextField
                        className={classes.search}
                        size="small"
                        variant="outlined"
                        label="Project No"
                        placeholder="e.g. NT0524142"
                        value={projectNo}
                        onChange={(e) => setProjectNo(e.target.value.toUpperCase())}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') loadHardwareBom();
                        }}
                    />
                    <Button className={classes.actionBtn} variant="contained" onClick={() => loadHardwareBom()}>
                        Search
                    </Button>
                    {activeProjectNo && (
                        <TextField
                            className={classes.search}
                            size="small"
                            variant="outlined"
                            label="Filter material"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                        />
                    )}
                </div>
            </div>

            {meta && (
                <div className={classes.summaryCards}>
                    <div className={classes.summaryCard}>
                        <div className={classes.summaryLabel}>Project</div>
                        <div className={classes.summaryValue}>{activeProjectNo}</div>
                    </div>
                    <div className={classes.summaryCard}>
                        <div className={classes.summaryLabel}>Total Items</div>
                        <div className={classes.summaryValue}>{meta.totalItems ?? items.length}</div>
                    </div>
                    <div className={classes.summaryCard}>
                        <div className={classes.summaryLabel}>Matched in Master</div>
                        <div className={classes.summaryValue}>{matchedCount}</div>
                    </div>
                    <div className={classes.summaryCard}>
                        <div className={classes.summaryLabel}>Source</div>
                        <div className={classes.summaryValue} style={{ fontSize: 12 }}>
                            IMOS Hardware File
                        </div>
                    </div>
                </div>
            )}

            <TableContainer component={Paper} className={classes.tableContainer}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <StyledTableCell>SN</StyledTableCell>
                            <StyledTableCell>Material / Piece</StyledTableCell>
                            <StyledTableCell>Sheet</StyledTableCell>
                            <StyledTableCell>Sub Category</StyledTableCell>
                            <StyledTableCell>BOM Qty</StyledTableCell>
                            <StyledTableCell>MTR Raised</StyledTableCell>
                            <StyledTableCell>MTR Factory</StyledTableCell>
                            <StyledTableCell>MTR Store</StyledTableCell>
                            <StyledTableCell>Remaining</StyledTableCell>
                            <StyledTableCell>Match</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredItems.map((item, index) => {
                            const remaining = Number(item.remainingMtrQuantity ?? 0);
                            return (
                                <TableRow key={`${getItemName(item)}-${index}`}>
                                    <StyledTableCell>{index + 1}</StyledTableCell>
                                    <StyledTableCell>{getItemName(item) || '—'}</StyledTableCell>
                                    <StyledTableCell>{item.sheetName || '—'}</StyledTableCell>
                                    <StyledTableCell>{item.subCategory || '—'}</StyledTableCell>
                                    <StyledTableCell>{item.bomQuantity ?? item.quantity ?? '—'}</StyledTableCell>
                                    <StyledTableCell>{item.mtrQuantity ?? 0}</StyledTableCell>
                                    <StyledTableCell>{item.mtr?.factory ?? 0}</StyledTableCell>
                                    <StyledTableCell>{item.mtr?.store ?? 0}</StyledTableCell>
                                    <StyledTableCell>
                                        <span className={remaining > 0 ? classes.remainingPositive : classes.remainingZero}>
                                            {remaining}
                                        </span>
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        {item.matchStatus ? (
                                            <span
                                                className={classes.matchChip}
                                                style={getMatchChipStyle(item.matchStatus)}
                                            >
                                                {item.matchStatus}
                                            </span>
                                        ) : '—'}
                                    </StyledTableCell>
                                </TableRow>
                            );
                        })}

                        {!filteredItems.length && (
                            <TableRow>
                                <StyledTableCell colSpan={10}>
                                    {activeProjectNo
                                        ? (filter
                                            ? `No materials match "${filter}".`
                                            : 'No hardware BOM items found for this project.')
                                        : 'Enter a project number and click Search.'}
                                </StyledTableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {meta && !items.length && (
                <Box mt={2}>
                    <Typography variant="body2" color="textSecondary">
                        {meta.message || 'Hardware BOM file may not be uploaded for this project.'}
                    </Typography>
                </Box>
            )}
        </div>
    );
};

const mapDispatchToProps = (dispatch) => ({
    setLoading: (value) => dispatch(setLoading(value)),
    setSnackInfo: (msg, severity) => dispatch(setSnackInfo(msg, severity)),
});

export default connect(null, mapDispatchToProps)(HardwareBomViewer);
